// Import Centre — CSV/XLSX bulk import for Clients, Jobs, Fleet, Suppliers.
// Wizard: pick type → upload → map columns → import → summary.
import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "./_shared";
import {
  Upload, Users, Hammer, Truck, Phone, Download, FileSpreadsheet,
  CheckCircle2, AlertTriangle, ArrowLeft, ArrowRight, RotateCcw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const KIND_META = {
  clients: { icon: Users, desc: "Your customer list — names, contacts, addresses." },
  jobs: { icon: Hammer, desc: "Active and upcoming jobs. Job numbers are assigned automatically." },
  fleet: { icon: Truck, desc: "Vehicles, machines and plant — regos, WoF/rego dates, rates." },
  suppliers: { icon: Phone, desc: "Your trade phone book — merchants, hire, subbies." },
};

export default function ImportData() {
  const [kinds, setKinds] = useState(null);
  const [kind, setKind] = useState(null);        // selected kind
  const [parsed, setParsed] = useState(null);    // parse response
  const [mapping, setMapping] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);    // commit response
  const fileRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  useEffect(() => {
    api.get("/import/kinds").then((r) => setKinds(r.data)).catch(() => setKinds([]));
  }, []);

  const reset = () => { setKind(null); setParsed(null); setMapping({}); setResult(null); };

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/import/parse?kind=${kind.kind}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setParsed(data);
      setMapping(data.suggested_mapping || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't read that file");
    } finally { setBusy(false); }
  };

  const doImport = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/import/commit", { import_id: parsed.import_id, mapping });
      setResult(data);
      toast.success(`${data.created} ${kind.label.toLowerCase()} imported`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally { setBusy(false); }
  };

  const missingRequired = useMemo(() => {
    if (!parsed) return [];
    return parsed.fields.filter((f) => f.required && !mapping[f.key]);
  }, [parsed, mapping]);

  const step = result ? 4 : parsed ? 3 : kind ? 2 : 1;

  return (
    <div className="p-4 lg:p-10 max-w-5xl mx-auto pb-24 lg:pb-10" data-testid="import-page">
      <PageHeader
        tag="/ Import"
        title="Import Data"
        subtitle="Moving from another platform? Bring your clients, jobs, fleet and suppliers across from a CSV or Excel file."
      />

      {/* Steps */}
      <div className="flex items-center gap-2 mt-4 mb-6 text-[10px] font-bold uppercase tracking-widest" data-testid="import-steps">
        {["Choose type", "Upload file", "Match columns", "Done"].map((s, i) => (
          <div key={s} className="flex items-center gap-2">
            <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-display ${step > i + 1 ? "bg-emerald-600 text-white" : step === i + 1 ? "bg-[#FF5F15] text-white" : "bg-zinc-200 dark:bg-white/10 text-zinc-500"}`}>
              {step > i + 1 ? "✓" : i + 1}
            </span>
            <span className={step === i + 1 ? "text-zinc-900 dark:text-white" : "text-zinc-400"}>{s}</span>
            {i < 3 && <span className="text-zinc-300 dark:text-zinc-600">—</span>}
          </div>
        ))}
      </div>

      {/* STEP 1: choose type */}
      {step === 1 && (
        kinds === null ? <div className="text-zinc-400 text-sm">Loading…</div> : (
          <div className="grid sm:grid-cols-2 gap-4" data-testid="import-kind-grid">
            {kinds.map((k) => {
              const M = KIND_META[k.kind] || {};
              const Icon = M.icon || FileSpreadsheet;
              return (
                <button key={k.kind} onClick={() => setKind(k)}
                  className="hard-card p-5 text-left hover:border-[#FF5F15] transition-colors group"
                  data-testid={`import-kind-${k.kind}`}>
                  <div className="flex items-center gap-3">
                    <span className="w-10 h-10 bg-[#FF5F15]/10 border border-[#FF5F15]/25 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-[#FF5F15]" />
                    </span>
                    <div>
                      <div className="font-display font-black uppercase tracking-tight">{k.label}</div>
                      <div className="text-[11px] text-zinc-500">{M.desc}</div>
                    </div>
                  </div>
                  <div className="mt-3 text-[10px] font-bold uppercase tracking-wider text-zinc-400 group-hover:text-[#FF5F15] inline-flex items-center gap-1">
                    Import {k.label} <ArrowRight className="w-3 h-3" />
                  </div>
                </button>
              );
            })}
          </div>
        )
      )}

      {/* STEP 2: upload */}
      {step === 2 && (
        <div className="max-w-2xl">
          <button onClick={reset} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-zinc-500 hover:text-[#FF5F15] mb-3" data-testid="import-back">
            <ArrowLeft className="w-3 h-3" /> Choose a different type
          </button>
          <div className="hard-card p-6" data-testid="import-upload-card">
            <div className="font-display font-black uppercase tracking-tight text-lg">Upload your {kind.label} file</div>
            <p className="text-xs text-zinc-500 mt-1">
              CSV or Excel (.xlsx), up to 2,000 rows. The first row must be your column headings — we'll match them to Fieldlink fields next, so your columns don't need to be in any particular order.
            </p>
            <button onClick={async () => {
                try {
                  const r = await api.get(`/import/template/${kind.kind}.csv`, { responseType: "blob" });
                  const url = URL.createObjectURL(r.data);
                  const a = document.createElement("a");
                  a.href = url; a.download = `fieldlink-${kind.kind}-template.csv`; a.click();
                  URL.revokeObjectURL(url);
                } catch { toast.error("Couldn't download template"); }
              }}
              className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-[#FF5F15] hover:underline"
              data-testid="import-template-link">
              <Download className="w-3.5 h-3.5" /> Download {kind.label} template
            </button>
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); upload(e.dataTransfer.files?.[0]); }}
              onClick={() => fileRef.current?.click()}
              className={`mt-4 border-2 border-dashed p-10 text-center cursor-pointer transition-colors ${dragOver ? "border-[#FF5F15] bg-[#FF5F15]/5" : "border-zinc-300 dark:border-white/15 hover:border-[#FF5F15]"}`}
              data-testid="import-dropzone">
              <Upload className="w-7 h-7 mx-auto text-zinc-400" />
              <div className="mt-2 text-sm font-bold">{busy ? "Reading file…" : "Drop your file here or click to browse"}</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 mt-1">.csv · .xlsx</div>
              <input ref={fileRef} type="file" accept=".csv,.xlsx,.xlsm,.txt" className="hidden"
                onChange={(e) => upload(e.target.files?.[0])} data-testid="import-file-input" />
            </div>
          </div>
        </div>
      )}

      {/* STEP 3: map columns + preview */}
      {step === 3 && (
        <div>
          <button onClick={() => { setParsed(null); setMapping({}); }} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-zinc-500 hover:text-[#FF5F15] mb-3" data-testid="import-back-upload">
            <ArrowLeft className="w-3 h-3" /> Upload a different file
          </button>
          <div className="grid lg:grid-cols-2 gap-4 items-start">
            <div className="hard-card p-5" data-testid="import-mapping-card">
              <div className="font-display font-black uppercase tracking-tight">Match your columns</div>
              <p className="text-[11px] text-zinc-500 mt-0.5 mb-3">
                {parsed.filename} · {parsed.row_count} rows. We've matched what we could — fix any gaps below.
              </p>
              <div className="space-y-2">
                {parsed.fields.map((f) => (
                  <div key={f.key} className="flex items-center gap-3">
                    <div className="w-40 shrink-0 text-[11px] font-semibold">
                      {f.label} {f.required && <span className="text-[#FF5F15]">*</span>}
                    </div>
                    <Select value={mapping[f.key] || "__skip__"}
                      onValueChange={(v) => setMapping((m) => ({ ...m, [f.key]: v === "__skip__" ? undefined : v }))}>
                      <SelectTrigger className="h-8 rounded-sm text-xs flex-1" data-testid={`import-map-${f.key}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="rounded-sm max-h-64">
                        <SelectItem value="__skip__">— Skip —</SelectItem>
                        {parsed.headers.map((h) => (
                          <SelectItem key={h} value={h}>{h}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
              {missingRequired.length > 0 && (
                <div className="mt-3 flex items-start gap-2 p-2 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/25 text-[11px]" data-testid="import-missing-required">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />
                  <span>Map the required field{missingRequired.length > 1 ? "s" : ""}: <b>{missingRequired.map((f) => f.label).join(", ")}</b></span>
                </div>
              )}
              <Button onClick={doImport} disabled={busy || missingRequired.length > 0}
                className="mt-4 w-full rounded-sm bg-[#FF5F15] hover:bg-[#e05512] font-bold uppercase tracking-wider"
                data-testid="import-commit-btn">
                {busy ? "Importing…" : `Import ${parsed.row_count} rows`}
              </Button>
              <div className="text-[10px] text-zinc-400 mt-2">Duplicates (matching an existing {kind.label.toLowerCase().replace(/s$/, "")}) are skipped automatically — safe to re-run.</div>
            </div>

            <div className="hard-card p-5 overflow-x-auto" data-testid="import-preview-card">
              <div className="label-tag mb-2">File preview (first {parsed.sample.length} rows)</div>
              <table className="text-[10px] w-full">
                <thead>
                  <tr>
                    {parsed.headers.map((h) => (
                      <th key={h} className={`text-left px-1.5 py-1 font-mono uppercase tracking-wider border-b border-zinc-200 dark:border-white/10 whitespace-nowrap ${Object.values(mapping).includes(h) ? "text-[#FF5F15]" : "text-zinc-400"}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {parsed.sample.map((r, i) => (
                    <tr key={i} className="border-b border-zinc-100 dark:border-white/5">
                      {parsed.headers.map((_h, j) => (
                        <td key={j} className="px-1.5 py-1 whitespace-nowrap max-w-[140px] truncate">{String(r[j] ?? "")}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* STEP 4: result */}
      {step === 4 && (
        <div className="max-w-2xl" data-testid="import-result">
          <div className="hard-card p-6">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              <div>
                <div className="font-display font-black uppercase tracking-tight text-xl" data-testid="import-result-created">
                  {result.created} {kind.label.toLowerCase()} imported
                </div>
                <div className="text-xs text-zinc-500">{result.skipped.length} of {result.total} rows skipped</div>
              </div>
            </div>
            {result.skipped.length > 0 && (
              <div className="mt-4">
                <div className="label-tag mb-1.5">Skipped rows</div>
                <div className="max-h-56 overflow-y-auto space-y-1" data-testid="import-skipped-list">
                  {result.skipped.map((s, i) => (
                    <div key={i} className="flex items-start gap-2 text-[11px] py-1 border-b border-zinc-100 dark:border-white/5">
                      <span className="font-mono text-[9px] text-zinc-400 shrink-0 w-14">Row {s.row}</span>
                      <span className="text-zinc-600 dark:text-zinc-300">{s.reason}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="mt-5 flex gap-2 flex-wrap">
              <Button variant="outline" className="rounded-sm font-bold uppercase tracking-wider" onClick={reset} data-testid="import-again-btn">
                <RotateCcw className="w-3.5 h-3.5 mr-1" /> Import more data
              </Button>
              <a href={`/app/${kind.kind === "suppliers" ? "suppliers" : kind.kind}`}>
                <Button className="rounded-sm bg-[#FF5F15] hover:bg-[#e05512] font-bold uppercase tracking-wider" data-testid="import-view-btn">
                  View {kind.label} <ArrowRight className="w-3.5 h-3.5 ml-1" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
